import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Search, Plus, Minus, Wrench, Package, ShoppingCart, Hammer, X, ListChecks, Sparkles, Flag } from 'lucide-react';
import KitSheetList, { AutoTag, FlagTag } from './KitSheetList';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/tool-crib`;

export const KIND_META = {
  tool: { label: 'Tool', short: 'Tools', icon: Wrench, cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  consumable: { label: 'Consumable', short: 'Consumables', icon: Package, cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' },
  purchase: { label: 'Purchased', short: 'Purchased', icon: ShoppingCart, cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  fabricated: { label: 'Fabricated', short: 'Fabricated', icon: Hammer, cls: 'bg-purple-100 text-purple-800 border-purple-300' },
};

// `inKit` = what's already on the list (green ✓n marks). `existing` = what the companion rules should treat as already there
// (defaults to inKit; a restock pass sends existing but no inKit so the sheet starts blank). onAdd(items[])
export default function KitItemPicker({ onAdd, onClose, title = 'Add items', inKit = {}, existing = null, hint = null }) {
  const rulesExisting = existing || inKit;
  const [q, setQ] = useState('');
  const [kind, setKind] = useState('sheet'); // 'sheet' = the scroll-and-tick tool list (default) · '' = search everything
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cart, setCart] = useState({}); // catalog_id (or custom-N) -> {item, qty} — survives switching tabs
  const [custom, setCustom] = useState({ kind: 'purchase', name: '', qty: 1, unit_cost: '', po_number: '' });
  const [auto, setAuto] = useState({}); // catalog_id -> {qty, name, rule, for} — what the rules will drop in with the current picks
  const [hints, setHints] = useState({}); // catalog_id -> {short, message, for} — flagged to check (wedge anchors → hammer drill / bit), not added
  const customSeq = useRef(0);

  // live companion preview: shows on the sheet (violet AUTO chips) as soon as a trigger is ticked, not after "Add to kit"
  useEffect(() => {
    const picks = Object.values(cart).filter(({ item }) => item.catalog_id).map(({ item, qty }) => ({ catalog_id: item.catalog_id, label: item.name, qty }));
    if (!picks.length) { setAuto({}); setHints({}); return undefined; }
    let on = true;
    const t = setTimeout(() => {
      axios.post(`${API}/rules/preview`, { items: picks, existing: Object.entries(rulesExisting).map(([catalog_id, qty]) => ({ catalog_id, qty })) })
        .then((r) => {
          if (!on) return;
          setAuto(Object.fromEntries((r.data.auto || []).map((a) => [a.catalog_id, a])));
          setHints(Object.fromEntries((r.data.hints || []).map((h) => [h.catalog_id, h])));
        }).catch(() => {});
    }, 300);
    return () => { on = false; clearTimeout(t); };
  }, [cart, rulesExisting]);

  useEffect(() => {
    if (kind === 'purchase' || kind === 'fabricated' || kind === 'sheet') { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/catalog`, { params: { q, kind } });
        setResults(r.data.items || []);
      } catch { setResults([]); }
      setLoading(false);
    }, 220);
    return () => clearTimeout(t);
  }, [q, kind]);

  const bump = (it, d) => setCart(c => {
    const cur = c[it.catalog_id]?.qty || 0;
    return withQty(c, it, Math.max(0, cur + d));
  });
  const withQty = (c, it, qty) => {
    const next = { ...c };
    if (!qty || qty <= 0) delete next[it.catalog_id]; else next[it.catalog_id] = { item: it, qty };
    return next;
  };
  const typeQty = (it, raw) => { if (raw === '') return; setCart(c => withQty(c, it, Math.max(0, Math.floor(Number(raw) || 0)))); };

  const cartToItems = () => Object.values(cart).map(({ item, qty }) => ({
    kind: item.kind, catalog_id: item.catalog_id || null, name: item.name, category: item.category || null,
    unit: item.unit || null, unit_cost: item.unit_cost ?? null, po_number: item.po_number || null, qty_requested: qty,
  }));
  // ONE "Add to kit" for everything picked — tools, consumables AND purchased/fabricated — so nothing is lost when switching tabs
  const addCart = () => {
    const items = cartToItems();
    if (items.length) onAdd(items);
    setCart({});
  };
  const closePicker = () => {
    if (cartCount) addCart(); // never drop what was picked
    onClose();
  };

  const addCustom = () => {
    if (!custom.name.trim()) return;
    const id = `custom-${++customSeq.current}`;
    setCart(c => ({ ...c, [id]: { item: { kind, catalog_id: null, name: custom.name.trim(), category: KIND_META[kind].label,
      unit: 'each', unit_cost: custom.unit_cost === '' ? null : Number(custom.unit_cost), po_number: custom.po_number || null }, qty: Number(custom.qty) || 1 } }));
    setCustom({ kind, name: '', qty: 1, unit_cost: '', po_number: '' });
  };

  const cartCount = Object.values(cart).reduce((s, c) => s + c.qty, 0);
  const cartLines = Object.entries(cart);
  const autoLines = Object.values(auto);
  const hintLines = Object.values(hints);
  const showCustom = kind === 'purchase' || kind === 'fabricated';
  const showSheet = kind === 'sheet';

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="kit-item-picker">
      <div className={`bg-white w-full ${showSheet ? 'sm:max-w-4xl h-[96vh] sm:h-[92vh]' : 'sm:max-w-2xl max-h-[92vh]'} rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col`}>
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
          <div className="min-w-0">
            <h3 className="text-base md:text-lg font-bold text-slate-900">{title}</h3>
            {hint && <p className="text-xs text-blue-800" data-testid="kit-picker-hint">{hint}</p>}
          </div>
          <button onClick={closePicker} className="p-2 rounded-lg hover:bg-slate-100 text-slate-500" title={cartCount ? `Adds the ${cartCount} picked item${cartCount !== 1 ? 's' : ''} and closes` : 'Close'} data-testid="kit-picker-close"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-4 pt-3 flex gap-2 flex-wrap">
          {[['sheet', 'Tool list', ListChecks], ['', 'Search', Search], ['purchase', 'Purchased', null], ['fabricated', 'Fabricated', null]].map(([k, l, Ic]) => (
            <button key={k} onClick={() => setKind(k)} data-testid={`kit-picker-kind-${k || 'all'}`}
              className={`px-3 py-2 rounded-full text-sm font-semibold border inline-flex items-center gap-1.5 ${kind === k ? 'bg-white text-slate-900 border-slate-200' : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'}`}>{Ic && <Ic className="w-4 h-4" />}{l}</button>
          ))}
        </div>
        {showSheet ? (
          <>
            <div className="px-4 py-2.5">
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-3 text-slate-500" />
                <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter the sheet… (or just scroll and tick)"
                  className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-300 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-blue-400" data-testid="kit-sheet-filter" />
              </div>
            </div>
            <KitSheetList cart={cart} bump={bump} inKit={inKit} auto={auto} hints={hints} query={q} />
          </>
        ) : !showCustom ? (
          <>
            <div className="px-4 py-3">
              <div className="relative">
                <Search className="w-5 h-5 absolute left-3 top-3 text-slate-500" />
                <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Search tools & consumables… (e.g. 7018, grinder, sling)"
                  className="w-full pl-10 pr-3 py-3 rounded-xl border border-slate-300 text-base text-slate-900 outline-none focus:ring-2 focus:ring-blue-400" data-testid="kit-picker-search" />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto px-4 pb-3 space-y-1.5" data-testid="kit-picker-results">
              {loading && <p className="text-sm text-slate-500 py-2">Searching…</p>}
              {!loading && results.length === 0 && <p className="text-sm text-slate-500 py-2">No matches — try a shorter word, or add a Purchased / Fabricated item.</p>}
              {results.map(it => {
                const Icon = KIND_META[it.kind].icon;
                const qty = cart[it.catalog_id]?.qty || 0;
                const au = !qty && auto[it.catalog_id];
                const hi = !qty && !au && hints[it.catalog_id];
                return (
                  <div key={`${it.kind}-${it.catalog_id}`} className={`flex items-center gap-3 px-3 py-2 rounded-xl border ${qty ? 'border-blue-400 bg-blue-50' : au ? 'border-violet-300 bg-violet-50' : hi ? 'border-amber-300 bg-amber-50' : 'border-slate-200'}`} data-testid={`kit-picker-row-${it.catalog_id}`}>
                    <Icon className={`w-4 h-4 flex-shrink-0 ${it.kind === 'tool' ? 'text-blue-600' : 'text-emerald-600'}`} />
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-900 truncate">{it.name}{au && <AutoTag qty={au.qty} a={au} testid={`kit-picker-auto-${it.catalog_id}`} />}{hi && <FlagTag h={hi} testid={`kit-picker-flag-${it.catalog_id}`} />}</p>
                      <p className="text-xs text-slate-500 truncate">{it.category}{it.unit_cost != null ? ` · $${Number(it.unit_cost).toFixed(2)}/${it.unit}` : ''}</p>
                    </div>
                    <div className="flex items-center gap-1">
                      {qty > 0 && <button onClick={() => bump(it, -1)} className="w-10 h-10 rounded-lg bg-slate-200 text-slate-800 flex items-center justify-center" data-testid={`kit-picker-minus-${it.catalog_id}`}><Minus className="w-4 h-4" /></button>}
                      {qty > 0 && <input type="number" inputMode="numeric" min="0" step="1" value={qty} onChange={e => typeQty(it, e.target.value)} onFocus={e => e.target.select()}
                        className="w-14 h-10 text-center font-bold text-slate-900 rounded-lg border border-blue-300 bg-white outline-none focus:ring-2 focus:ring-blue-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        title="Type the quantity" data-testid={`kit-picker-qty-${it.catalog_id}`} />}
                      <button onClick={() => bump(it, 1)} className="w-10 h-10 rounded-lg bg-blue-600 text-white flex items-center justify-center" data-testid={`kit-picker-plus-${it.catalog_id}`}><Plus className="w-5 h-5" /></button>
                      {it.kind === 'consumable' && [10, 25, 50].map(n => (
                        <button key={n} onClick={() => typeQty(it, n)} className={`h-10 px-2 rounded-lg text-xs font-bold border ${qty === n ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-700 border-slate-300 hover:border-blue-400'}`} title={`Set to ${n}`} data-testid={`kit-picker-quick-${n}-${it.catalog_id}`}>{n}</button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="px-4 py-4 space-y-3" data-testid="kit-picker-custom">
            <p className="text-sm text-slate-600">{kind === 'purchase' ? 'Material bought for this job — it gets costed to the timesheet if it doesn\'t come back.' : 'Something the shop fabricated for this job — optional cost, costed if it stays on site.'}</p>
            <input value={custom.name} onChange={e => setCustom({ ...custom, kind, name: e.target.value })} placeholder={kind === 'purchase' ? 'e.g. 3" sch40 pipe 21ft ×2, Unistrut, Anchors box' : 'e.g. Motor mount bracket, Guard panel'}
              className="w-full px-3 py-3 rounded-xl border border-slate-300 text-base text-slate-900 outline-none focus:ring-2 focus:ring-blue-400" data-testid="kit-custom-name" />
            <div className="grid grid-cols-3 gap-2">
              <label className="text-xs text-slate-600">Qty<input type="number" min="0" step="1" value={custom.qty} onChange={e => setCustom({ ...custom, qty: e.target.value })} className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-300 text-slate-900" data-testid="kit-custom-qty" /></label>
              <label className="text-xs text-slate-600">Cost each ($)<input type="number" min="0" step="0.01" value={custom.unit_cost} onChange={e => setCustom({ ...custom, unit_cost: e.target.value })} placeholder="optional" className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-300 text-slate-900" data-testid="kit-custom-cost" /></label>
              <label className="text-xs text-slate-600">PO #<input value={custom.po_number} onChange={e => setCustom({ ...custom, po_number: e.target.value })} placeholder="optional" className="mt-1 w-full px-3 py-2.5 rounded-lg border border-slate-300 text-slate-900" data-testid="kit-custom-po" /></label>
            </div>
            <button onClick={addCustom} disabled={!custom.name.trim()} className="w-full py-3 rounded-xl bg-white hover:bg-slate-50 disabled:bg-slate-300 text-slate-900 font-bold text-base" data-testid="kit-custom-add">+ Put {KIND_META[kind].label.toLowerCase()} item in the list</button>
          </div>
        )}
        {/* shared footer: what's picked so far (all tabs) + the single Add button */}
        <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 rounded-b-2xl" data-testid="kit-picker-footer">
          {cartLines.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2 max-h-20 overflow-y-auto" data-testid="kit-picker-cart">
              {cartLines.map(([id, { item, qty }]) => (
                <span key={id} className="inline-flex items-center gap-1 text-xs bg-white border border-slate-300 rounded-full pl-2 pr-1 py-0.5 text-slate-800" data-testid={`kit-picker-cart-${id}`}>
                  <b>{qty}×</b> {item.name}{item.unit_cost != null && item.catalog_id == null ? ` · $${Number(item.unit_cost).toFixed(2)}` : ''}
                  <button onClick={() => setCart(c => { const n = { ...c }; delete n[id]; return n; })} className="w-5 h-5 rounded-full hover:bg-slate-200 text-slate-500 flex items-center justify-center" title="Remove" data-testid={`kit-picker-cart-remove-${id}`}><X className="w-3 h-3" /></button>
                </span>
              ))}
              {autoLines.map((a) => (
                <span key={`auto-${a.catalog_id}`} className="inline-flex items-center gap-1 text-xs bg-violet-50 border border-violet-300 rounded-full px-2 py-0.5 text-violet-900" title={`${a.rule} — comes with ${(a.for || []).join(', ')}`} data-testid={`kit-picker-cart-auto-${a.catalog_id}`}>
                  <Sparkles className="w-3 h-3 text-violet-600" /><b>{a.qty}×</b> {a.name}
                </span>
              ))}
              {hintLines.map((h) => (
                <span key={`hint-${h.catalog_id}`} className="inline-flex items-center gap-1 text-xs bg-amber-50 border border-amber-300 rounded-full px-2 py-0.5 text-amber-900" title={h.message} data-testid={`kit-picker-cart-hint-${h.catalog_id}`}>
                  <Flag className="w-3 h-3 text-amber-600" />{h.name}? <span className="text-amber-700">— {h.message.replace(/^You have /, '').replace(/ — need .*$/, '')}</span>
                </span>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-slate-600">{cartCount ? `${cartCount} item${cartCount !== 1 ? 's' : ''} picked (${cartLines.length} line${cartLines.length !== 1 ? 's' : ''})${autoLines.length ? ` · +${autoLines.length} auto` : ''}${hintLines.length ? ` · ⚑ ${hintLines.length} to check` : ''}` : 'Tap + on tools/consumables, or put in a purchased / fabricated item'}</span>
            <button onClick={addCart} disabled={!cartCount} className="px-5 py-3 rounded-xl bg-emerald-600 disabled:bg-slate-300 text-white font-bold text-base whitespace-nowrap" data-testid="kit-picker-add">Add {cartCount || ''} to kit</button>
          </div>
        </div>
      </div>
    </div>
  );
}
